import React from "react";
import { Link } from "react-router-dom";
import { Code2, Stethoscope, Cpu, Building2, ArrowRight, CheckCircle2 } from "lucide-react";
import SEO, { breadcrumb } from "../components/SEO";

const SECTIONS = [
  {
    id: "editeurs",
    icon: <Code2 size={26} />,
    tag: "Éditeurs",
    title: "Éditeurs de logiciels métier",
    desc: "Intégrez rapidement des fonctionnalités de télésanté conformes au sein de vos solutions existantes. L'architecture permet d'accélérer le développement de nouveaux services numériques sans porter seul la complexité réglementaire et l'intégration aux services nationaux.",
    bullets: [
      "API & SDK documentés (REST, webhooks)",
      "Activation modulaire des services nationaux",
      "Marketplace de modules prêts à l'emploi",
      "Support DPO & accompagnement de mise en conformité",
    ],
  },
  {
    id: "professionnels",
    icon: <Stethoscope size={26} />,
    tag: "Professionnels",
    title: "Professionnels de santé",
    desc: "Déployez des parcours patients numériques sécurisés pour améliorer la coordination des soins, faciliter les échanges entre professionnels et simplifier la gestion des documents et des données de santé.",
    bullets: [
      "Coordination temps réel multi-professionnels",
      "Messagerie MSSanté intégrée",
      "Téléconsultation & téléexpertise",
      "Synchronisation DMP / Mon Espace Santé",
    ],
  },
  {
    id: "industriels",
    icon: <Cpu size={26} />,
    tag: "Industriels",
    title: "Industriels de santé",
    desc: "Accélérez la mise sur le marché de solutions numériques innovantes en vous appuyant sur une infrastructure e-santé prête à l'emploi, interopérable avec l'écosystème national et adaptée aux exigences réglementaires.",
    bullets: [
      "Time-to-market accéléré",
      "Conformité HDS, RGPD, PGSSI-S",
      "Études cliniques & évaluations PECAN/PECAT",
      "Industrialisation à l'échelle",
    ],
  },
  {
    id: "institutions",
    icon: <Building2 size={26} />,
    tag: "Institutions & CPTS",
    title: "Institutions & CPTS",
    desc: "Coordonnez les parcours de soins territoriaux grâce à une plateforme capable de connecter les acteurs de santé, faciliter les échanges d'informations et structurer les organisations de soins à l'échelle d'un territoire.",
    bullets: [
      "Annuaires & rôles territoriaux",
      "Cartographie des parcours",
      "Tableaux de bord territoriaux",
      "Conformité aux feuilles de route régionales",
    ],
  },
];

export default function Solutions() {
  return (
    <div style={{ background: "#F8FAFC" }} data-testid="page-solutions">
      <SEO
        title="Solutions e-santé : éditeurs, soignants, CPTS · MetaConnect"
        description="Solutions adaptées aux éditeurs de logiciels métier, aux professionnels de santé, aux industriels et aux CPTS. Interopérabilité native INS, DMP, MSSanté."
        canonical="/solutions"
        schema={breadcrumb([
          { name: "Accueil", url: "/" },
          { name: "Solutions", url: "/solutions" },
        ])}
      />

      {/* Hero — split 50/50 navy + white */}
      <section className="relative pt-16" data-testid="solutions-hero">
        <div className="grid lg:grid-cols-2 min-h-[480px]">
          {/* LEFT — Navy */}
          <div
            className="px-6 sm:px-10 lg:px-16 py-16 lg:py-20 flex flex-col justify-center"
            style={{ background: "#0F2B5B" }}
          >
            <div
              className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full"
              style={{
                background: "rgba(79,195,247,0.12)",
                border: "1px solid rgba(79,195,247,0.30)",
                color: "#4FC3F7",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              Solutions
            </div>
            <h1
              className="mt-6 font-display"
              style={{
                color: "#ffffff",
                fontSize: "clamp(32px, 4.2vw, 52px)",
                fontWeight: 600,
                lineHeight: 1.08,
                letterSpacing: "-0.018em",
              }}
            >
              Une réponse adaptée à chaque{" "}
              <span style={{ color: "#4FC3F7" }}>acteur de santé</span>.
            </h1>
            <p className="mt-6 max-w-md" style={{ color: "#93B4D4", fontSize: 16, lineHeight: 1.55 }}>
              MetaConnect accompagne l'ensemble de l'écosystème santé : éditeurs, professionnels,
              industriels, institutions. À chaque profil, des solutions et un accompagnement spécifiques.
            </p>
          </div>
          {/* RIGHT — White */}
          <div
            className="px-6 sm:px-10 lg:px-14 py-16 lg:py-20 flex flex-col justify-center"
            style={{ background: "#ffffff", borderLeft: "0.5px solid #E8EDF5" }}
          >
            <div className="grid grid-cols-2 gap-4">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="p-5 transition-all hover:-translate-y-0.5"
                  style={{
                    background: "#F0F9FF",
                    border: "0.5px solid #BAE6FD",
                    borderRadius: 12,
                    textDecoration: "none",
                  }}
                  data-testid={`solutions-hero-card-${s.id}`}
                >
                  <div
                    className="w-10 h-10 rounded-lg grid place-items-center"
                    style={{ background: "#ffffff", border: "0.5px solid #BAE6FD", color: "#0F2B5B" }}
                  >
                    {s.icon}
                  </div>
                  <div className="mt-3 text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#4FC3F7" }}>
                    {s.tag}
                  </div>
                  <div className="mt-1 text-[14px]" style={{ color: "#0F2B5B", fontWeight: 500 }}>
                    {s.title.split(" ").slice(0, 3).join(" ")}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sections — light theme */}
      <section className="py-16 lg:py-24" style={{ background: "#F8FAFC" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid gap-7">
          {SECTIONS.map((s) => (
            <article
              key={s.id}
              id={s.id}
              data-testid={`solution-${s.id}`}
              className="p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-start scroll-mt-24"
              style={{
                background: "#ffffff",
                border: "0.5px solid #E8EDF5",
                borderRadius: 12,
                boxShadow: "0 1px 2px rgba(15,43,91,0.04)",
              }}
            >
              <div className="lg:col-span-4">
                <div
                  className="grid place-items-center h-14 w-14 rounded-xl"
                  style={{ background: "#F0F9FF", border: "0.5px solid #BAE6FD", color: "#0F2B5B" }}
                >
                  {s.icon}
                </div>
                <div className="mt-4 text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#4FC3F7" }}>
                  {s.tag}
                </div>
                <h2 className="mt-2 font-display text-2xl lg:text-3xl" style={{ color: "#0F2B5B", fontWeight: 500, letterSpacing: "-0.015em" }}>
                  {s.title}
                </h2>
              </div>
              <div className="lg:col-span-8">
                <p className="text-[15px] leading-relaxed" style={{ color: "#64748B" }}>{s.desc}</p>
                <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-[14px]" style={{ color: "#0F2B5B" }}>
                      <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: "#4FC3F7" }} />
                      {b}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/demo"
                  className="mt-7 inline-flex items-center gap-2"
                  style={{
                    background: "#0F2B5B",
                    color: "#ffffff",
                    borderRadius: 999,
                    padding: "10px 22px",
                    fontSize: 13,
                    fontWeight: 500,
                    textDecoration: "none",
                    boxShadow: "0 8px 22px -10px rgba(15,43,91,0.45)",
                  }}
                >
                  Discuter de mon projet <ArrowRight size={14} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
